import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Search, ArrowRight, Users, Headset } from "lucide-react";
import { api } from "@/lib/api";
import { brandStyle } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SpecialistMatcher from "@/components/SpecialistMatcher";
import SpecialistRequestDialog from "@/components/SpecialistRequestDialog";
import { PublicVehicleCard, DealerNav, DealerFooter } from "@/components/PublicVehicleCard";
import { NewArrivalsAlert } from "@/components/LeadCapture";
import { VinconexLogo } from "@/components/VinconexMark";

export default function DealerPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [matcherOpen, setMatcherOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sp] = useSearchParams();

  useEffect(() => {
    api.get(`/public/dealers/${slug}`, { params: { tour: sp.get("tour") || undefined } }).then(({ data }) => { setData(data); setState("ok"); }).catch(() => setState("notfound"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0B0E]"><Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] flex flex-col items-center justify-center text-center px-6" data-testid="dealer-not-found">
        <VinconexLogo variant="full" className="h-28 w-auto" />
        <h1 className="font-heading text-3xl font-black text-white mt-8">This dealership page isn't live yet</h1>
        <p className="text-slate-400 mt-3 max-w-md">It may still be awaiting approval. Check back soon.</p>
        <Link to="/" className="mt-6"><Button className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">Back to Vinconex</Button></Link>
      </div>
    );
  }

  const { dealer, vehicles, new_arrivals, specialist_count } = data;
  const arrivalsCount = data.new_arrivals_count ?? new_arrivals.length;
  const matchEnabled = !!dealer.features?.specialist_match && specialist_count > 0;
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? vehicles.filter((v) => `${v.year} ${v.make} ${v.model} ${v.trim} ${v.vin} ${v.stock_number} ${v.exterior_color}`.toLowerCase().includes(needle))
    : vehicles;

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white grid-noise" style={brandStyle(dealer.accent_color)} data-testid="dealer-public-page">
      <DealerNav dealer={dealer} />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand-soft text-xs font-mono-tech uppercase tracking-widest mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-brand animate-pulse" /> Live inventory · {vehicles.length} vehicles
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]" data-testid="dealer-name-heading">{dealer.name}</h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl">
            {matchEnabled
              ? "Browse what's on the lot right now, see what just arrived, and get matched with the specialist who fits how you like to shop."
              : "Browse what's on the lot right now, see what just arrived, and talk to a product specialist whenever you're ready."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/d/${dealer.slug}/new-arrivals`}>
              <Button size="lg" data-testid="new-arrivals-cta" className="bg-brand hover:bg-brand/90 text-white font-bold h-12 px-6 shadow-lg shadow-brand/30 transition-all hover:-translate-y-0.5">
                <Sparkles className="mr-2 h-4 w-4" /> See new arrivals{arrivalsCount > 0 ? ` (${arrivalsCount})` : ""}
              </Button>
            </Link>
            {matchEnabled ? (
              <Button size="lg" variant="outline" onClick={() => setMatcherOpen(true)} data-testid="dealer-match-btn" className="h-12 px-6 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-100">
                <Users className="mr-2 h-4 w-4" /> Match me with a specialist
              </Button>
            ) : (
              <Button size="lg" variant="outline" onClick={() => setMatcherOpen(true)} data-testid="dealer-talk-btn" className="h-12 px-6 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-100">
                <Headset className="mr-2 h-4 w-4" /> Talk to a specialist
              </Button>
            )}
          </div>
        </motion.div>
      </section>

      {new_arrivals.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="new-arrivals-strip">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand" /> Just arrived</h2>
            <Link to={`/d/${dealer.slug}/new-arrivals`} data-testid="new-arrivals-view-all" className="text-sm text-brand-soft hover:text-white flex items-center gap-1">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {new_arrivals.slice(0, 4).map((v) => <PublicVehicleCard key={v.id} v={v} showAdded />)}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <NewArrivalsAlert dealer={dealer} />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">All inventory</h2>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="dealer-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search model, trim, color…" className="pl-9 bg-slate-950 border-slate-800 focus:border-brand text-slate-100" />
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-slate-500 py-16 text-center border border-dashed border-[#2A2E3D] rounded-xl">No vehicles match that search.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dealer-inventory-grid">
            {filtered.map((v) => <PublicVehicleCard key={v.id} v={v} />)}
          </div>
        )}
      </section>

      <DealerFooter dealer={dealer} />
      {matchEnabled ? (
        <SpecialistMatcher open={matcherOpen} onOpenChange={setMatcherOpen} vehicle={null} dealer={dealer} />
      ) : (
        <SpecialistRequestDialog open={matcherOpen} onOpenChange={setMatcherOpen} vehicle={null} dealer={dealer} />
      )}
    </div>
  );
}
